/**
 * Error Utilities — Shared helpers for error handling across the app
 * 
 * Centralises the `toFriendlyMessage` function that was previously
 * duplicated in every thunk file.
 */

import { logger } from '../services/logger';
import { MapiError, RequestError, UnauthorizedError } from '../errors';

/**
 * Extract the correlationId from an error object, if present.
 */
export function extractCorrelationId(error: unknown): string | undefined {
    return (error as any)?.correlationId as string | undefined;
}

/**
 * Convert a raw error into a user-friendly message.
 * 
 * Rules:
 *  1. MapiError        → use its structured `message` if reasonable, else fallback
 *  2. UnauthorizedError → "Session expired – please sign in again."
 *  3. RequestError      → use its message (already user-friendly from apiClient)
 *  4. Generic Error     → hide status codes / technical jargon; use the fallback
 *  5. Unknown           → fallback
 * 
 * The real error is always logged for developers via the Logger service.
 * 
 * @param error    – the caught error (any type)
 * @param fallback – user-facing fallback sentence, e.g. "Unable to load subscriptions"
 * @param source   – optional log source tag, e.g. "SubscriptionThunk"
 */
export function toFriendlyMessage(
    error: unknown,
    fallback: string,
    source?: string
): string {
    // Always log the real error for devs
    if (error instanceof Error) {
        logger.trackError(error, source ?? 'toFriendlyMessage');
    } else {
        logger.trackError(new Error(String(error)), source ?? 'toFriendlyMessage');
    }

    // ── Typed errors from apiClient already carry clean messages ──
    if (error instanceof UnauthorizedError) {
        return 'Session expired – please sign in again.';
    }

    // Check by class name as fallback (instanceof can fail with bundlers)
    const errorName = (error as any)?.name;
    const statusCode = (error as any)?.statusCode;

    if (error instanceof RequestError || errorName === 'RequestError') {
        // For 5xx server errors, prefer the caller's fallback message (more context-specific)
        if (statusCode && statusCode >= 500) {
            return `${fallback}. Please try again later.`;
        }
        // Other RequestError messages are already user-friendly (set in apiClient)
        const msg = (error as any).message || '';
        return msg || `${fallback}. Please try again.`;
    }

    if (error instanceof MapiError || errorName === 'MapiError') {
        // MapiError.message comes from the server; use it only when it's
        // not a raw status-code string.
        const msg = (error as any).message || '';
        if (msg && !/status code/i.test(msg)) {
            return msg;
        }
        return `${fallback}. Please try again.`;
    }

    // ── Generic / unknown errors ─────────────────────────────────
    if (error instanceof Error) {
        const msg = error.message || '';
        // Suppress raw HTTP codes / network jargon
        if (
            /status code/i.test(msg) ||
            /network error/i.test(msg) ||
            /timeout/i.test(msg) ||
            /ECONNREFUSED/i.test(msg)
        ) {
            return `${fallback}. Please try again.`;
        }
        // If the message looks user-readable, use it
        if (msg.length > 0 && msg.length < 200) {
            return msg;
        }
    }

    return fallback;
}

/**
 * Convenience: create a friendly Error object suitable for re-throwing
 * from thunks so that UI catch blocks get a clean message.
 * Preserves the original error as `.cause` for debugging.
 */
export function createFriendlyError(
    error: unknown,
    fallback: string,
    source?: string
): Error {
    const friendly = new Error(toFriendlyMessage(error, fallback, source));
    (friendly as any).cause = error;
    return friendly;
}
